import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Heart, Users, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import DonationCard from '../components/DonationCard';

const About = () => {
  const [donationInfo, setDonationInfo] = useState({
    donation_url: null,
    donation_qr_image: null,
    suggested_amounts: [],
    is_available: false
  });

  useEffect(() => {
    fetchDonationInfo();
  }, []);

  const fetchDonationInfo = async () => {
    try {
      const response = await axios.get(`${API}/settings/donation`);
      setDonationInfo(response.data || { is_available: false });
    } catch (err) {
      console.error('fetchDonationInfo failed', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="about-page">
      {/* Simple Header */}
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#8B2E3C] rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-[#222222]">Kindness Circle</span>
            </Link>
            <Link to="/" className="text-[#8B2E3C] flex items-center gap-2 hover:underline">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-4">
            Community Kindness Circle
          </h1>
          <p className="text-lg text-[#666666] max-w-2xl mx-auto">
            Connecting families with meaningful volunteer opportunities in Ossipee, Effingham, and the Carroll County region.
          </p>
        </div>

        {/* Mission Section */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-[#222222] mb-4">Our Mission</h2>
          <p className="text-[#666666] mb-4">
            The Community Kindness Circle exists to make volunteering accessible, trackable, and rewarding for families in our community. We believe that small acts of kindness, when organized and celebrated, can transform our neighborhoods.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 mt-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FBF4EA] rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-[#8B2E3C]" />
              </div>
              <h3 className="font-bold text-[#222222] mb-1">Browse Opportunities</h3>
              <p className="text-sm text-[#666666]">Find volunteer events that match your schedule</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FBF4EA] rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-[#8B2E3C]" />
              </div>
              <h3 className="font-bold text-[#222222] mb-1">Sign Up & Volunteer</h3>
              <p className="text-sm text-[#666666]">Join events and make a difference</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FBF4EA] rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-6 h-6 text-[#8B2E3C]" />
              </div>
              <h3 className="font-bold text-[#222222] mb-1">Track Your Hours</h3>
              <p className="text-sm text-[#666666]">Log and celebrate your community impact</p>
            </div>
          </div>
        </div>

        {/* Partner Organizations */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-[#222222] mb-4">Partner Organizations</h2>
          <p className="text-[#666666] mb-4">
            We work with local nonprofits, food pantries, schools, and community groups to coordinate volunteer opportunities. Partner organizations can manage their own events, track attendance, and generate reports.
          </p>
          <p className="text-sm text-[#666666]">
            Interested in becoming a partner? Contact your local Community Kindness Circle administrator.
          </p>
        </div>

        {/* Support Section */}
        <DonationCard
          donationUrl={donationInfo.donation_url}
          donationQrImage={donationInfo.donation_qr_image}
          suggestedAmounts={donationInfo.suggested_amounts || []}
          isAvailable={donationInfo.is_available}
          showTitle={true}
        />

        {/* Footer */}
        <div className="mt-12 pt-8 border-t-2 border-[#E9DDCF] text-center">
          <p className="text-sm text-[#666666]">
            © {new Date().getFullYear()} Community Kindness Circle. Built with ❤️ for our community.
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/" className="text-[#8B2E3C] text-sm hover:underline">Home</Link>
            <Link to="/login" className="text-[#8B2E3C] text-sm hover:underline">Sign In</Link>
            <Link to="/register" className="text-[#8B2E3C] text-sm hover:underline">Register</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
